import * as z from 'zod'

import type {
  AffiliateCreateCouponSchema as GeneratedCreateCouponSchema,
  AffiliateUpdateCouponSchema as GeneratedUpdateCouponSchema,
} from '@/api/gen'
import { rowsToCsv } from '@/utils/csv'
import { parsePercent } from '@/utils/parse-percent'

/**
 * Single owner of the affiliate "link" (coupon) create/edit vocabulary for
 * MKT-745: the plan taxonomy the scope selector spans, the pure scope resolver
 * that turns a chosen scope into concrete `plan_ids`, the zod form schemas
 * (single-create, batch-create, edit), and the request builders that assemble
 * the wire bodies. Field rules live here, never inline in the dialog (see
 * `.claude/rules/forms.md`); the dialog only wires react-hook-form to these.
 *
 * The backend is the ultimate authority — every write is re-validated server
 * side. This client validation catches mistakes before the round-trip.
 */

// TODO(MKT-745): After gto-wizard#2819 lands, regenerate the affiliates Orval
// client and reconcile these temporary coupon/tag overrides. The same regen owes
// `tag_ids` / `untagged` on ListPartnerCouponsParams (shipped in gto-wizard#2827,
// still absent from the checked-in client — see `partner-coupon-hooks.ts`).

export const affiliateCouponTagSchema = z.object({
  id: z.uuid(),
  name: z.string().min(1).max(64),
  color_hex: z.string().regex(/^#[0-9A-Fa-f]{6}$/),
  created_at: z.string(),
})

export type AffiliateCouponTagSchema = z.infer<typeof affiliateCouponTagSchema>

export const couponTagCreateFormSchema = z.object({
  name: z.string().trim().min(1, 'coupon.tags.nameRequired').max(64, 'coupon.tags.nameTooLong'),
  color_hex: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'coupon.tags.colorInvalid'),
})

export type AffiliateCreateCouponTagRequest = z.infer<typeof couponTagCreateFormSchema>

/**
 * Per-code redemption funnel, derived server-side from reservations. `stats` is
 * order-derived and so says nothing about codes attempted but never paid; this
 * is what answers "is the code still usable".
 *
 * `in_flight` is a CURRENT snapshot (the hold is roughly the 1h Adyen session
 * TTL), never a historical total — surface it as live state.
 */
export const affiliateCouponReservationsSchema = z.object({
  /** Reservations ever created, all-time, whatever became of them. */
  attempted: z.number(),
  /** Reservations consumed by a payment. */
  converted: z.number(),
  /** Reservations that expired without ever being paid. */
  abandoned: z.number(),
  /** Live, unexpired and unconsumed right now. */
  in_flight: z.number(),
  last_attempt_at: z.string().nullable(),
})

export type AffiliateCouponReservationsSchema = z.infer<typeof affiliateCouponReservationsSchema>

/**
 * Stand-in for a coupon nobody has ever tried. Purely defensive: the backend
 * resolves this field for unannotated instances too — `resolve_reservations`
 * falls back to zeros — so it is present on write responses as well as reads, and
 * the default only tolerates a backend predating the aggregation. Frozen because
 * every parse that does fall back shares this one object (see `stats` below).
 */
export const ZERO_COUPON_RESERVATIONS: AffiliateCouponReservationsSchema = Object.freeze({
  attempted: 0,
  converted: 0,
  abandoned: 0,
  in_flight: 0,
  last_attempt_at: null,
})

/**
 * Per-code usage and revenue, aggregated server-side over NON-refunded
 * commissions. Order-derived, so — unlike `reservations` — it says nothing about
 * codes that were attempted but never paid.
 */
export const affiliateCouponStatsSchema = z.object({
  total_orders: z.number(),
  // Decimal money: a string on the wire, a number from a hand-built fixture —
  // same union `discount_value` uses. Narrowed at the render site by `toAmount`.
  total_revenue_usd: z.union([z.number(), z.string()]),
  total_commission_usd: z.union([z.number(), z.string()]),
})

export type AffiliateCouponStatsSchema = z.infer<typeof affiliateCouponStatsSchema>

/** Stand-in for a coupon with no orders, defensive for the same reason as
 *  `ZERO_COUPON_RESERVATIONS`: `resolve_stats` is a resolver on a required field
 *  that returns zeros for an unannotated instance, so the backend always sends
 *  this — the default tolerates one that doesn't, rather than describing today's
 *  wire shape. */
export const ZERO_COUPON_STATS: AffiliateCouponStatsSchema = Object.freeze({
  total_orders: 0,
  total_revenue_usd: 0,
  total_commission_usd: 0,
})

export const affiliateAdminCouponSchema = z.object({
  id: z.string(),
  code: z.string(),
  tags: z.array(affiliateCouponTagSchema),
  discount_value: z.union([z.number(), z.string()]),
  is_active: z.boolean(),
  plan_ids: z.array(z.number()),
  max_uses: z.number().nullable(),
  is_one_use_per_user: z.boolean(),
  first_payment_only: z.boolean(),
  days_valid: z.number().nullable(),
  // Fail closed: silently defaulting a missing billing-lifecycle field could misrepresent renewal behavior.
  auto_cancelled: z.boolean(),
  payment_methods: z.array(z.string()).nullable(),
  // Shared by every code minted in one batch-create call; null for a single code.
  // A model column, so it is always serialized — nullable describes the wire, while
  // the default is only tolerance for a response that predates the field.
  batch_id: z.uuid().nullable().default(null),
  created_at: z.string(),
  // Zod 4 hands a value default straight back without re-parsing it, so every
  // fallback shares one object rather than getting a clone. Both constants are
  // frozen, which makes that safe by construction instead of by audit.
  stats: affiliateCouponStatsSchema.default(ZERO_COUPON_STATS),
  reservations: affiliateCouponReservationsSchema.default(ZERO_COUPON_RESERVATIONS),
})

export type AffiliateAdminCouponSchema = z.infer<typeof affiliateAdminCouponSchema>

export type AffiliateCreateCouponSchema = Omit<GeneratedCreateCouponSchema, 'tag'> & {
  tag_ids?: string[]
  auto_cancelled?: boolean
}

export type AffiliateUpdateCouponSchema = Omit<GeneratedUpdateCouponSchema, 'tag'> & {
  tag_ids?: string[]
}

// ─── Plan taxonomy ────────────────────────────────────────────────────────
// Mirrors the affiliate-admin plan catalog
// (GET /v4/affiliates/admin/subscription-plans/ → AffiliateAdminSubscriptionPlanSchema:
// id, interval, tier, type, variant, version). Enums mirror the payments API
// (libs/api-client GameVariant / SubscriptionTier / SubscriptionInterval).

export const PLAN_INTERVALS = ['MONTHLY', 'YEARLY', 'DAILY'] as const
export type PlanInterval = (typeof PLAN_INTERVALS)[number]

export type CouponPlan = {
  id: number
  interval: PlanInterval
  tier: string
  type: string
  variant: string | null
  version: number
}

// Coupon scoping only offers paid, purchasable plans on the two real billing
// intervals. FREE / TRIAL tiers and the DAILY interval are excluded so a link
// can never pin a non-purchasable plan — and, by pinning exactly one interval,
// can never mix monthly + yearly (the MKT-745 "no annual+monthly mixing" rule).
export const ELIGIBLE_TIERS = ['STARTER', 'PREMIUM', 'ELITE', 'ULTRA'] as const
export type EligibleTier = (typeof ELIGIBLE_TIERS)[number]

export const ELIGIBLE_INTERVALS = ['MONTHLY', 'YEARLY'] as const
export type EligibleInterval = (typeof ELIGIBLE_INTERVALS)[number]

// Widened to `ReadonlySet<string>` so `.has()` accepts any string (an O(1)
// membership test with no cast — the values stay the narrow literal tuples above).
const ELIGIBLE_TIER_SET: ReadonlySet<string> = new Set(ELIGIBLE_TIERS)
const ELIGIBLE_INTERVAL_SET: ReadonlySet<string> = new Set(ELIGIBLE_INTERVALS)

export const isEligibleTier = (tier: string): tier is EligibleTier => ELIGIBLE_TIER_SET.has(tier)

export const isEligibleInterval = (interval: string): interval is EligibleInterval =>
  ELIGIBLE_INTERVAL_SET.has(interval)

export const isEligiblePlan = (plan: CouponPlan): boolean =>
  isEligibleTier(plan.tier) && isEligibleInterval(plan.interval)

// A logical plan is identified by product (variant + type) + tier + interval;
// the same logical plan can exist at several `version`s.
const logicalKey = (plan: CouponPlan): string =>
  `${plan.variant ?? ''}|${plan.type}|${plan.tier}|${plan.interval}`

/**
 * Filter to eligible plans and collapse each logical plan to its current
 * (highest) version.
 *
 * The affiliate-admin catalog can carry several `version`s of one logical plan:
 * SubscriptionPlan's DB uniqueness is (interval, type, tier, version, variant), so
 * a (variant|type|tier|interval) key repeats across versions and `version` is a
 * monotonic integer (higher = newer). Coupon scoping targets the current plan, so
 * we keep the highest `version` per logical key.
 */
export const toEligiblePlans = (plans: CouponPlan[]): CouponPlan[] => {
  const currentByKey = new Map<string, CouponPlan>()
  for (const plan of plans) {
    if (!isEligiblePlan(plan)) {
      continue
    }
    const key = logicalKey(plan)
    const existing = currentByKey.get(key)
    if (existing === undefined || plan.version > existing.version) {
      currentByKey.set(key, plan)
    }
  }

  return [...currentByKey.values()]
}

const DEPRECATED_SELECTABLE_PLAN_TYPES = new Set(['HUSNG', 'MTT', 'SPINS', 'STRADDLE_ANTE_CASH'])

const isSelectablePlan = (plan: CouponPlan): boolean =>
  !DEPRECATED_SELECTABLE_PLAN_TYPES.has(plan.type)

export const selectablePlans = (plans: CouponPlan[]): CouponPlan[] =>
  toEligiblePlans(plans).filter(isSelectablePlan)

// ─── Products (variant + type) — the "Same product" axis ────────────────────

export type PlanProduct = { variant: string | null; type: string }

export const productKey = (product: PlanProduct): string =>
  `${product.variant ?? ''}|${product.type}`

export const parseProductKey = (key: string): PlanProduct | null => {
  const sep = key.indexOf('|')
  if (sep === -1) {
    return null
  }
  const variant = key.slice(0, sep)
  const type = key.slice(sep + 1)
  if (type === '') {
    return null
  }

  return { variant: variant === '' ? null : variant, type }
}

/** Distinct products among selectable plans, ordered by first appearance. */
export const eligibleProducts = (plans: CouponPlan[]): PlanProduct[] => {
  const seen = new Map<string, PlanProduct>()
  for (const plan of selectablePlans(plans)) {
    seen.set(productKey({ variant: plan.variant, type: plan.type }), {
      variant: plan.variant,
      type: plan.type,
    })
  }

  return [...seen.values()]
}

// ─── Plan scope ─────────────────────────────────────────────────────────────
// The four create scopes, plus a `custom` variant used ONLY when reconstructing
// an existing coupon whose stored `plan_ids` don't map to any of the four (a
// legacy or backend-authored set). `custom` preserves that set verbatim so
// editing an unrelated field (e.g. discount) never silently broadens or narrows
// the restriction — it is never offered as a create option.

export type PlanScope =
  | { kind: 'all' }
  | { kind: 'specific'; planId: number }
  | { kind: 'tier'; tier: EligibleTier; interval: EligibleInterval }
  | { kind: 'product'; variant: string | null; type: string; interval: EligibleInterval }
  | { kind: 'custom'; planIds: number[] }

export type PlanScopeKind = PlanScope['kind']

// The three scoped kinds (excludes `all` / `custom`, which don't filter the
// eligible catalog).
type ScopedPlanScope = Extract<PlanScope, { kind: 'specific' | 'tier' | 'product' }>

/**
 * Filter an ALREADY-eligible plan list by a scoped kind. Takes pre-deduped
 * plans so callers that iterate scopes (e.g. `inferPlanScope`) dedup the catalog
 * once, not once per candidate scope.
 */
const filterEligibleByScope = (scope: ScopedPlanScope, eligible: CouponPlan[]): CouponPlan[] => {
  switch (scope.kind) {
    case 'specific':
      return eligible.filter((plan) => plan.id === scope.planId)
    case 'tier':
      return eligible.filter((plan) => plan.tier === scope.tier && plan.interval === scope.interval)
    case 'product':
      return eligible.filter(
        (plan) =>
          plan.variant === scope.variant &&
          plan.type === scope.type &&
          plan.interval === scope.interval,
      )
  }
}

/**
 * The plans a scope resolves to, as objects (drives the live preview). Every
 * scoped mode pins exactly one interval, so a resolved set never mixes monthly
 * and yearly. `custom` matches against the full list (not just eligible) so a
 * legacy set that includes now-ineligible plans still previews every member.
 */
export const resolveScopedPlans = (scope: PlanScope, plans: CouponPlan[]): CouponPlan[] => {
  if (scope.kind === 'all') {
    return []
  }
  if (scope.kind === 'custom') {
    const ids = new Set(scope.planIds)

    return plans.filter((plan) => ids.has(plan.id))
  }

  return filterEligibleByScope(scope, toEligiblePlans(plans))
}

/**
 * The concrete `plan_ids` a scope resolves to. `all` ⇒ `[]` (backend treats an
 * empty list as "all plans"). `custom` returns its stored ids verbatim so an
 * unrepresentable legacy set round-trips unchanged.
 */
export const resolvePlanIds = (scope: PlanScope, plans: CouponPlan[]): number[] => {
  if (scope.kind === 'all') {
    return []
  }
  if (scope.kind === 'custom') {
    return [...scope.planIds]
  }

  return resolveScopedPlans(scope, plans).map((plan) => plan.id)
}

/**
 * True when a chosen scope resolves to NO plans — submitting it would send
 * `plan_ids: []`, which the backend reads as "all plans" and would silently
 * broaden the discount. `all` legitimately resolves to [] and `custom` always
 * carries its verbatim ids, so neither is ever flagged. Callers use this to
 * block the submit / disable the CTA.
 */
export const scopeMatchesNoPlans = (scope: PlanScope, plans: CouponPlan[]): boolean =>
  scope.kind !== 'all' && scope.kind !== 'custom' && resolvePlanIds(scope, plans).length === 0

/**
 * Reconstruct the scope of an existing coupon from its stored `plan_ids`.
 * Priority (narrowest first): all → selectable single → exact tier-span → exact
 * product-span → custom. A set produced by the create UI always maps back to
 * its original scope; any other set falls through to `custom` and is preserved
 * verbatim.
 */
export const inferPlanScope = (planIds: number[], plans: CouponPlan[]): PlanScope => {
  if (planIds.length === 0) {
    return { kind: 'all' }
  }
  const target = new Set(planIds)
  const isSameSet = (ids: number[]): boolean =>
    ids.length === target.size && ids.every((id) => target.has(id))
  const eligible = toEligiblePlans(plans)
  const idsForScope = (scope: ScopedPlanScope): number[] =>
    filterEligibleByScope(scope, eligible).map((plan) => plan.id)

  // Narrowest-first: a single stored id reconstructs as `specific` BEFORE any
  // tier/product span. A one-plan tier (or product) span matches the same
  // single-id set, but reconstructing it as `tier` would silently BROADEN the
  // discount if that span later gains a plan and the admin re-saves — pinning
  // the id keeps the coupon to that one plan. `specific` applies ONLY when the
  // id is still in the selectable catalog; a stale-version / ineligible id — or
  // any id when the catalog is empty or failed to load — falls through to
  // `custom`, which preserves it verbatim (catalog-independent) so a saved edit
  // never writes `plan_ids: []` (= "all plans" on the backend).
  const [only] = planIds
  if (
    planIds.length === 1 &&
    only !== undefined &&
    eligible.some((plan) => plan.id === only && isSelectablePlan(plan))
  ) {
    return { kind: 'specific', planId: only }
  }

  for (const tier of ELIGIBLE_TIERS) {
    for (const interval of ELIGIBLE_INTERVALS) {
      const ids = idsForScope({ kind: 'tier', tier, interval })
      if (ids.length > 0 && isSameSet(ids)) {
        return { kind: 'tier', tier, interval }
      }
    }
  }
  for (const product of eligibleProducts(eligible)) {
    for (const interval of ELIGIBLE_INTERVALS) {
      const ids = idsForScope({
        kind: 'product',
        variant: product.variant,
        type: product.type,
        interval,
      })
      if (ids.length > 0 && isSameSet(ids)) {
        return { kind: 'product', variant: product.variant, type: product.type, interval }
      }
    }
  }

  return { kind: 'custom', planIds: [...planIds] }
}

/**
 * Compose the ` - `-separated raw string that `<PlanName>` / `parsePlanName`
 * expect: GAME - FORMAT - TIER - BILLING (variant is the game token, type the
 * format token). Unknown tokens are tolerated by the parser.
 */
export const planNameString = (plan: CouponPlan): string =>
  [plan.variant, plan.type, plan.tier, plan.interval].filter((token) => token != null).join(' - ')

/**
 * Resolve a coupon's `plan_ids` to display names against the plan catalog for
 * the Links-table Scope column. Empty `planIds` means "all plans" (the caller
 * renders that sentinel); an id the catalog does not carry is dropped rather
 * than shown as a raw number.
 */
export const scopePlanNames = (
  planIds: readonly number[],
  plans: readonly CouponPlan[],
): string[] =>
  planIds
    .map((id) => plans.find((plan) => plan.id === id))
    .filter((plan): plan is CouponPlan => plan != null)
    .map(planNameString)

// ─── Form validation (zod) ──────────────────────────────────────────────────
// Numeric inputs are validated as strings — the MUI number field yields strings
// — and coerced to numbers only in the request builders. Every message is an
// i18n key resolved by the dialog via `formatMessage` (mirrors payout-methods).

const V = {
  codeRequired: 'coupon.validation.codeRequired',
  discountRequired: 'coupon.validation.discountRequired',
  discountRange: 'coupon.validation.discountRange',
  maxUsesRange: 'coupon.validation.maxUsesRange',
  daysValidRange: 'coupon.validation.daysValidRange',
  countRange: 'coupon.validation.countRange',
  prefix: 'coupon.validation.prefix',
  scopePlanRequired: 'coupon.validation.scopePlanRequired',
  scopeTierRequired: 'coupon.validation.scopeTierRequired',
  scopeProductRequired: 'coupon.validation.scopeProductRequired',
  scopeIntervalRequired: 'coupon.validation.scopeIntervalRequired',
} as const

const isIntInRange = (value: string, min: number, max: number): boolean => {
  const n = Number(value.trim())

  return Number.isInteger(n) && n >= min && n <= max
}

// Delegates to `parsePercent` so the schema and the zero-discount warning below
// can never disagree about what counts as a valid percent.
const isPercent = (value: string): boolean => parsePercent(value, { allowEmpty: false }).ok

/**
 * End-user sign-up discount (%) a NEW single link is pre-filled with — the
 * standard content-partner offer (MKT-441). The backend has no default of its
 * own (`AffiliateCoupon.discount_value` is a required, un-defaulted decimal), so
 * this is the product decision and it lives here rather than in a dialog: the
 * create form seeds its `discountValue` from it AND the 0%-warning copy
 * interpolates it, so the two can never disagree about what "the default" is.
 */
export const DEFAULT_COUPON_DISCOUNT_PERCENT = 10

/**
 * True when a discount field holds a value the buyer would receive as no
 * discount at all. `discount_value` is stored at two decimal places and rounds
 * half-to-even, so everything at or below 0.005 lands on 0.00 — `0.004` is a 0%
 * link, not a tiny one. (0.006 and up round to 0.01, a real if negligible
 * discount, so they are deliberately NOT flagged.) An empty field is not a zero
 * — that is the resolver's `discountRequired` — and junk or out-of-range input
 * is the resolver's business too; both return false here.
 */
export const isEffectivelyZeroDiscount = (value: string): boolean => {
  const parsed = parsePercent(value, { allowEmpty: true })

  return parsed.ok && parsed.value !== null && parsed.value <= 0.005
}

const codeField = z.string().trim().min(1, V.codeRequired).max(64, V.codeRequired)
const discountField = z.string().min(1, V.discountRequired).refine(isPercent, V.discountRange)
// An EMPTY value is a deliberate state, not an error: empty max_uses = unlimited
// redemptions, empty days_valid = no expiry window — both serialize to `null`
// (see `buildCoupon*Request`). A non-empty value must be an int in range.
const maxUsesField = z
  .string()
  .refine((v) => v.trim() === '' || isIntInRange(v, 1, 1_000_000), V.maxUsesRange)
const daysValidField = z
  .string()
  .refine((v) => v.trim() === '' || isIntInRange(v, 1, 3650), V.daysValidRange)
const countField = z.string().refine((v) => isIntInRange(v, 1, 500), V.countRange)
// Optional — empty is allowed. If present it prefixes the generated code, so it
// is kept to a code-safe uppercase alphanumeric charset, max 32 chars.
const codePrefixField = z
  .string()
  .refine((v) => v.trim() === '' || /^[A-Z0-9]{1,32}$/.test(v.trim()), V.prefix)
const tagIdsField = z.array(z.uuid())

// Single source of truth for the flat scope fields every coupon form carries.
// Both the scope-only `ScopeFormFields` view (below) and the shared form schema
// derive from this, so there is no hand-maintained parallel type to drift.
const scopeFieldsSchema = z.object({
  scopeKind: z.enum(['all', 'specific', 'tier', 'product', 'custom']),
  scopePlanId: z.string(),
  scopeTier: z.string(),
  scopeProduct: z.string(),
  scopeInterval: z.string(),
  // Carries the verbatim ids for a reconstructed `custom` scope (edit only).
  customPlanIds: z.array(z.number()),
})

type ScopeFormFields = z.infer<typeof scopeFieldsSchema>

const refineScope = (data: ScopeFormFields, ctx: z.RefinementCtx): void => {
  switch (data.scopeKind) {
    case 'all':
    case 'custom':
      return
    case 'specific':
      if (data.scopePlanId.trim() === '') {
        ctx.addIssue({ code: 'custom', message: V.scopePlanRequired, path: ['scopePlanId'] })
      }

      return
    case 'tier':
      if (!isEligibleTier(data.scopeTier)) {
        ctx.addIssue({ code: 'custom', message: V.scopeTierRequired, path: ['scopeTier'] })
      }
      if (!isEligibleInterval(data.scopeInterval)) {
        ctx.addIssue({ code: 'custom', message: V.scopeIntervalRequired, path: ['scopeInterval'] })
      }

      return
    case 'product':
      if (parseProductKey(data.scopeProduct) === null) {
        ctx.addIssue({ code: 'custom', message: V.scopeProductRequired, path: ['scopeProduct'] })
      }
      if (!isEligibleInterval(data.scopeInterval)) {
        ctx.addIssue({ code: 'custom', message: V.scopeIntervalRequired, path: ['scopeInterval'] })
      }
  }
}

// The fields every coupon form shares (tags, discount, plan-scope, expiry, and the two
// redemption toggles). The three concrete schemas below `.extend()` this base,
// so each is a structural superset BY CONSTRUCTION — the shared field components
// read this exact view via `useFormContext`, and `CouponSharedFormValues` is
// inferred from it, so a shared field can never drift out from under them (see
// `.claude/rules/forms.md` — schema is the SSOT).
const couponSharedFormSchema = scopeFieldsSchema.extend({
  tagIds: tagIdsField,
  discountValue: discountField,
  daysValid: daysValidField,
  isOneUsePerUser: z.boolean(),
  firstPaymentOnly: z.boolean(),
})

export type CouponSharedFormValues = z.infer<typeof couponSharedFormSchema>

// Renewal is immutable after creation, so only the two create schemas extend
// the shared form with it; the edit schema cannot accidentally submit it.
const couponCreateSharedFormSchema = couponSharedFormSchema.extend({
  autoRenewal: z.boolean(),
})

export type CouponCreateSharedFormValues = z.infer<typeof couponCreateSharedFormSchema>

export const couponSingleFormSchema = couponCreateSharedFormSchema
  .extend({
    code: codeField,
    maxUses: maxUsesField,
  })
  .superRefine(refineScope)

export const couponBatchFormSchema = couponCreateSharedFormSchema
  .extend({
    count: countField,
    codePrefix: codePrefixField,
  })
  .superRefine(refineScope)

export const couponEditFormSchema = couponSharedFormSchema
  .extend({
    code: codeField,
    maxUses: maxUsesField,
    isActive: z.boolean(),
  })
  .superRefine(refineScope)

export type CouponSingleFormValues = z.infer<typeof couponSingleFormSchema>
export type CouponBatchFormValues = z.infer<typeof couponBatchFormSchema>
export type CouponEditFormValues = z.infer<typeof couponEditFormSchema>

// ─── Scope builder ──────────────────────────────────────────────────────────

/**
 * Assemble a `PlanScope` from the flat form fields. Returns `null` when the
 * chosen scope is incomplete — the zod resolver rejects that case first, so a
 * validated form always yields a scope; `null` is the belt-and-suspenders guard
 * that keeps the builder total without a silent-broadening fallback.
 */
export const buildPlanScope = (values: ScopeFormFields): PlanScope | null => {
  switch (values.scopeKind) {
    case 'all':
      return { kind: 'all' }
    case 'custom':
      return { kind: 'custom', planIds: [...values.customPlanIds] }
    case 'specific': {
      const planId = Number(values.scopePlanId)

      return Number.isInteger(planId) ? { kind: 'specific', planId } : null
    }
    case 'tier':
      return isEligibleTier(values.scopeTier) && isEligibleInterval(values.scopeInterval)
        ? { kind: 'tier', tier: values.scopeTier, interval: values.scopeInterval }
        : null
    case 'product': {
      const product = parseProductKey(values.scopeProduct)

      return product !== null && isEligibleInterval(values.scopeInterval)
        ? {
            kind: 'product',
            variant: product.variant,
            type: product.type,
            interval: values.scopeInterval,
          }
        : null
    }
  }
}

/**
 * The concrete `plan_ids` a validated form should submit, or `null` when the
 * selection must NOT be submitted — either the scope is incomplete
 * (`buildPlanScope` → null) or a scoped selection resolves to zero plans (which
 * would send `plan_ids: []`, read by the backend as "all plans", silently
 * broadening the discount). The single guard shared by every coupon form's
 * submit handler and the submit button's disabled state, so the "never broaden"
 * rule lives in exactly one place.
 */
export const resolveSubmitPlanIds = (
  values: ScopeFormFields,
  plans: CouponPlan[],
): number[] | null => {
  const scope = buildPlanScope(values)
  if (scope === null || scopeMatchesNoPlans(scope, plans)) {
    return null
  }

  return resolvePlanIds(scope, plans)
}

// ─── Request builders ────────────────────────────────────────────────────────

// A cleared numeric field is a real value, not zero: the backend types
// `max_uses` / `days_valid` as `number | null` where null = "unlimited" /
// "never expires". `Number('')` is 0 (NOT NaN), so blindly coercing an empty
// field would silently cap a coupon at 0 uses — send `null` instead, matching
// the wire contract.
const toNullableNumber = (value: string): number | null => {
  const trimmed = value.trim()

  return trimmed === '' ? null : Number(trimmed)
}

// The coupon-batch request contract (MKT-745). `max_uses` is fixed at 1 server
// side for batch, so it is not sent. `days_valid` is nullable (null = no expiry
// window), matching the coupon schema. The batch endpoint is a hand-authored
// shim (src/api/coupon-batch-hooks.ts) until orval picks it up.
export type CouponBatchRequest = {
  count: number
  code_prefix?: string
  tag_ids?: string[]
  discount_value: number
  plan_ids?: number[]
  is_one_use_per_user: boolean
  first_payment_only: boolean
  days_valid: number | null
  auto_cancelled: boolean
}

export const buildCouponCreateRequest = (
  values: CouponSingleFormValues,
  planIds: number[],
): AffiliateCreateCouponSchema => {
  return {
    code: values.code.trim(),
    tag_ids: values.tagIds,
    discount_value: Number(values.discountValue),
    plan_ids: planIds,
    max_uses: toNullableNumber(values.maxUses),
    is_one_use_per_user: values.isOneUsePerUser,
    first_payment_only: values.firstPaymentOnly,
    days_valid: toNullableNumber(values.daysValid),
    auto_cancelled: !values.autoRenewal,
  }
}

export const buildCouponBatchRequest = (
  values: CouponBatchFormValues,
  planIds: number[],
): CouponBatchRequest => {
  const prefix = values.codePrefix.trim()

  return {
    count: Number(values.count),
    ...(prefix === '' ? {} : { code_prefix: prefix }),
    tag_ids: values.tagIds,
    discount_value: Number(values.discountValue),
    plan_ids: planIds,
    is_one_use_per_user: values.isOneUsePerUser,
    first_payment_only: values.firstPaymentOnly,
    days_valid: toNullableNumber(values.daysValid),
    auto_cancelled: !values.autoRenewal,
  }
}

// Update mirrors create (plus the active toggle); the same fields keep single
// edit at full parity with single create. `max_uses` / `days_valid` preserve a
// stored null (unlimited / never expires) rather than fabricating a value.
export const buildCouponUpdateRequest = (
  values: CouponEditFormValues,
  planIds: number[],
): AffiliateUpdateCouponSchema => ({
  code: values.code.trim(),
  tag_ids: values.tagIds,
  discount_value: Number(values.discountValue),
  plan_ids: planIds,
  max_uses: toNullableNumber(values.maxUses),
  is_one_use_per_user: values.isOneUsePerUser,
  first_payment_only: values.firstPaymentOnly,
  days_valid: toNullableNumber(values.daysValid),
  is_active: values.isActive,
})

// ─── Batch results CSV ───────────────────────────────────────────────────────

const csvPlansCell = (planIds: number[]): string =>
  planIds.length === 0 ? 'All plans' : `${planIds.length} plans`

const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000

/**
 * Absolute expiry instant (creation + days_valid), or null when it never
 * expires. Shared by the CSV export and the admin Expiry column so the two
 * can't drift — the column once showed a duration while the CSV showed a date.
 */
export const couponExpiresAt = (
  coupon: Pick<AffiliateAdminCouponSchema, 'created_at' | 'days_valid'>,
): Date | null =>
  coupon.days_valid === null
    ? null
    : new Date(new Date(coupon.created_at).getTime() + coupon.days_valid * MILLISECONDS_PER_DAY)

const csvExpirationCell = (coupon: AffiliateAdminCouponSchema): string => {
  const expiresAt = couponExpiresAt(coupon)

  return expiresAt === null ? 'Never' : expiresAt.toISOString()
}

/** Build the "Download CSV" payload for a generated batch (client-side). */
export const buildCouponBatchCsv = (coupons: AffiliateAdminCouponSchema[]): string => {
  const header = ['Code', 'Discount %', 'Expires at (UTC)', 'Plans']
  const rows = coupons.map((coupon) => [
    coupon.code,
    String(coupon.discount_value),
    csvExpirationCell(coupon),
    csvPlansCell(coupon.plan_ids),
  ])

  return rowsToCsv([header, ...rows])
}
